import moment from 'moment'

/**
 * Event fields the diets/costs actually depend on. Free-text columns (city,
 * registration number, document number, comments) are edited keystroke by
 * keystroke and must not trigger a recalculation.
 */
const ALLOWANCE_RELEVANT_EVENT_FIELDS = [
  'date',
  'tripEvent',
  'country',
  'vehicleType',
  'isTransit',
]

const eventSignature = (event: any): string => {
  if (!event) return ''
  const date = event.date
    ? typeof event.date === 'string'
      ? event.date
      : String(moment(event.date).valueOf())
    : ''
  const country = event.country?.id ?? event.country?.code ?? ''
  return [
    date,
    event.tripEvent ?? '',
    country,
    event.vehicleType ?? '',
    event.isTransit ? '1' : '0',
  ].join('|')
}

/**
 * True when patching one event with `updates` changes something the diets/costs
 * depend on. The value comparison matters as much as the field list: the events
 * grid commits date and time on blur, so clicking Save re-commits the value the
 * field already held and would otherwise queue a pointless recalculation.
 */
export const affectsAllowances = (
  event: any,
  updates: Record<string, any>,
): boolean => {
  const touchesRelevantField = Object.keys(updates || {}).some((key) =>
    ALLOWANCE_RELEVANT_EVENT_FIELDS.includes(key),
  )
  if (!touchesRelevantField) return false
  return eventSignature({ ...(event || {}), ...updates }) !== eventSignature(event)
}


/**
 * True when replacing the whole event list changes something the diets/costs
 * depend on. Guards against bookkeeping rewrites — the chart backfills stable
 * UIDs on first render — triggering a pointless recalculation.
 */
export const eventsAffectAllowances = (
  previous: any[] = [],
  next: any[] = [],
): boolean => {
  if ((previous?.length || 0) !== (next?.length || 0)) return true
  return (next || []).some(
    (event, index) =>
      eventSignature(event) !== eventSignature((previous || [])[index]),
  )
}

export const toExchangeRateDate = (value: any): string => {
  if (!value) return ''
  if (typeof value === 'string') return value.split('T')[0]
  const parsed = moment(value)
  return parsed.isValid() ? parsed.format('YYYY-MM-DD') : ''
}
